import { useTheme } from '@mui/material/styles';
import { Card, Stack, Typography } from 'common/components/material';
import Accordion, {
  AccordionDetails,
  AccordionSummary,
} from 'common/components/material/Accordion';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useEffect, useState } from 'react';
import {
  GridCardSkeletonView,
  RoundedEdgeCard,
} from 'features/matching-engine/matching-engine.utils';
import { PerformanceRankLabels } from 'features/matching-engine/matching-engine.utils';
import { useGetProfile } from 'features/profiles';
import {
  getCategorizedSkills,
  SkillFeedbackCardList,
  useGetAllFeedbackSkills,
} from './ManageSkillDashboardDrawer';
import { CardContent } from 'common/components/material/Card';
import { PerformanceMetadata } from 'features/matching-engine/matching-engine.utils';
import { useGetPerformanceReviews } from './MatchingEngineSearchView';
import { RectangularSkeleton } from 'common/components/loaders';
import { useFeatureAccess } from 'features/feature-access/hooks/useFeatureAccess';
import { FeaturesEnum } from 'features/feature-access/features.enum';
import {
  PageContainerTabContent,
  PageContainerTabHeader,
} from 'common/components/page-container';

export const FeedbackOverview: React.FC<{
  profileId: string;
}> = ({ profileId }) => {
  const { canRead: hasAccessToPerformanceData } = useFeatureAccess(
    FeaturesEnum.PERFORMANCE_RANK,
  );

  return (
    <Stack spacing={2}>
      {hasAccessToPerformanceData && <PerformanceReviewList profileId={profileId} />}
      <SkillFeedbackSection profileId={profileId} />
    </Stack>
  );
};

export const AccordionList: React.FC<{
  children: React.ReactNode;
  label: string;
}> = ({ children, label }) => {
  const [expanded, setExpanded] = useState<string | false>(label);
  const handleToggle = (panel: string) => () => {
    setExpanded(expanded === panel ? false : panel);
  };

  return (
    <Card sx={{ border: 'none' }}>
      <CardContent sx={{ px: 0, py: '0px !important' }}>
        <Accordion
          expanded={expanded === label}
          onChange={handleToggle(label)}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<NavigateNextIcon />}
            sx={{
              flexDirection: 'row-reverse',
              paddingX: 0,
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(270deg)',
              },
              '& .MuiAccordionSummary-content': {
                display: 'flex',
                alignItems: 'left',
                width: '100%',
                margin: 0,
              },
            }}
          >
            <Typography fontWeight="600" fontSize="0.95rem">
              {label}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, px: 1 }}>{expanded && children}</AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

const PerformanceReviewList: React.FC<{
  profileId: string;
  isDrawerComponent?: boolean;
}> = ({ profileId, isDrawerComponent = true }) => {
  const { palette } = useTheme();
  const { data: performance, isLoading } = useGetPerformanceReviews(profileId) as {
    data: PerformanceMetadata[];
    isLoading: boolean;
  };

  const [expandedCards, setExpandedCards] = useState<string[]>([]);

  // Automatically expand the first card
  useEffect(() => {
    if (performance.length > 0) {
      const firstCard = performance[0].rank?.toString() || 'card-0';
      setExpandedCards([firstCard]);
    }
  }, [performance]);

  const handleToggle = (card: string) => () => {
    setExpandedCards((prev) =>
      prev.includes(card) ? prev.filter((p) => p !== card) : [...prev, card],
    );
  };

  let body = <RectangularSkeleton height="5rem" />;

  if (!isLoading) {
    body = (
      <Stack spacing={1}>
        {performance.length > 0 ? (
          performance.map((review, index) => {
            const cardId = review.rank?.toString() || `card-${index}`;

            return (
              <RoundedEdgeCard key={cardId}>
                <CardContent
                  sx={{
                    padding: '0px',
                    marginX: '8px',
                    paddingBottom: '5px !important',
                  }}
                >
                  <Accordion
                    expanded={expandedCards.includes(cardId)}
                    onChange={handleToggle(cardId)}
                    disableGutters
                    elevation={0}
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<NavigateNextIcon />}
                      sx={{
                        paddingX: 0,
                        '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                          transform: 'rotate(270deg)',
                        },
                        '& .MuiAccordionSummary-content': {
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          margin: 0,
                        },
                      }}
                    >
                      <Typography
                        fontWeight="500"
                        fontSize="0.85rem"
                        marginLeft="1.2rem"
                        color={palette.Text.Caption}
                      >
                        {PerformanceRankLabels[review.rank]}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 1 }}>
                      <PerformanceReviewCards profileId={profileId} performance={review} />
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
              </RoundedEdgeCard>
            );
          })
        ) : (
          <NoDataMessage />
        )}
      </Stack>
    );
  }

  return isDrawerComponent ? (
    <AccordionList label={'Performance'}>{body}</AccordionList>
  ) : (
    <PageContainerTabContent>
      <PageContainerTabHeader title="Performance" />
      {body}
    </PageContainerTabContent>
  );
};

const NoDataMessage = () => {
  const { palette } = useTheme();
  return (
    <Typography fontStyle={'italic'} color={palette.Text.Caption} variant={'body3'}>
      No data available
    </Typography>
  );
};

const PerformanceReviewCards: React.FC<{
  profileId: string;
  performance: PerformanceMetadata;
}> = ({ profileId, performance }) => {
  const { palette } = useTheme();
  const {
    customerRecommendationScore,
    peerRecommendationScore,
    managerRecommendationScore,
    recommendationComments,
    performanceRating,
    performanceReason,
    potentialRating,
    potentialReason,
    criticalityRating,
    criticalityReason,
  } = performance;

  const { data: profileData, isLoading } = useGetProfile({
    id: profileId,
  });
  if (isLoading) return <GridCardSkeletonView cardsCount={10} gap={'2rem'} />;

  const { firstName, lastName } = profileData;
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          How likely is {fullName} to be recommended to a customer or stakeholder to have on
          their project?
        </Typography>
        <Typography variant="body4">{customerRecommendationScore}/10</Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          How likely is {fullName} to be recommended to a colleague to have on their team?
        </Typography>
        <Typography variant="body4">{peerRecommendationScore}/10</Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          How likely is {fullName} to be recommended to a manager to lead a project or
          initiative?
        </Typography>
        <Typography variant="body4">{managerRecommendationScore}/10</Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          Ratings Reason
        </Typography>
        <Typography variant="body4">{recommendationComments || '-'}</Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          Performance Rating
        </Typography>
        <Typography variant="body4">
          {performanceRating != null ? `${performanceRating}/5` : '-'}
        </Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          Performance Reason
        </Typography>
        <Typography variant="body4">{performanceReason || '-'}</Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          Potential Rating
        </Typography>
        <Typography variant="body4">
          {potentialRating != null ? `${potentialRating}/5` : '-'}
        </Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          Potential Reason
        </Typography>
        <Typography variant="body4">{potentialReason || '-'}</Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          Criticality Rating
        </Typography>
        <Typography variant="body4">
          {criticalityRating != null ? `${criticalityRating}/5` : '-'}
        </Typography>
      </Stack>

      <Stack spacing={0.3}>
        <Typography variant="body4" color={palette.Text.Caption}>
          Criticality Reason
        </Typography>
        <Typography variant="body4">{criticalityReason || '-'}</Typography>
      </Stack>
    </Stack>
  );
};

export const SkillFeedbackSection: React.FC<{
  profileId: string;
}> = ({ profileId }) => {
  return (
    <AccordionList label={'Skill Feedback'}>
      <SkillFeedbackAccordionList profileId={profileId} />
    </AccordionList>
  );
};

export const SkillFeedbackAccordionList: React.FC<{
  profileId: string;
}> = ({ profileId }) => {
  const { data: skillsDemonstrated, isLoading } = useGetAllFeedbackSkills(profileId);

  if (isLoading) {
    return <RectangularSkeleton height="5rem" />;
  }

  const { confirmedSkills } = getCategorizedSkills(skillsDemonstrated || []);

  if (!confirmedSkills.length) return <NoDataMessage />;
  return (
    <Stack spacing={2}>
      <SkillFeedbackCardList skillCategory={confirmedSkills} profileId={profileId} />
    </Stack>
  );
};

export default PerformanceReviewList;
